import routes from "routes";
import {
  ROLE_ACCOUNT,
  ROLE_ADMIN,
  ROLE_BDM,
  ROLE_CLIENT,
  ROLE_EMPLOYEE_MANAGER,
  ROLE_EMPLOYEE_MEMBER,
  ROLE_MASTER_DATA_ADMIN,
} from "AppConstants";

// Actions usage analytics counts (HRMS-322). Keys are lowercase so the server accepts them.
// The two add-activity keys count successful saves, not dialog opens.
// @since NEXT_VERSION
export const FEATURE_ADD_ACTIVITY = "timesheet.add-activity";
export const FEATURE_ADD_MULTIPLE_ACTIVITIES = "timesheet.add-multiple-activities";
export const FEATURE_ADD_LEAVE = "timesheet.add-leave";
export const FEATURE_ACTIVITY_EDITED = "timesheet.activity-edited";
export const FEATURE_ACTIVITY_DELETED = "timesheet.activity-deleted";
export const FEATURE_APPROVE = "timesheet.approve";
export const FEATURE_REJECT = "timesheet.reject";
export const FEATURE_CLIENT_APPROVE = "timesheet.client-approve";
export const FEATURE_EXPORT_CONCISE = "export.concise";
export const FEATURE_EXPORT_DETAILED = "export.detailed";
export const FEATURE_EXPORT_CONSOLIDATED = "export.consolidated";
export const FEATURE_EXPORT_CONCISE_MONTHLY = "export.concise-monthly";
export const FEATURE_EXPORT_UTILIZATION = "export.utilization";
export const FEATURE_USER_GUIDE = "help.user-guide";

const ALL_ROLES = [
  ROLE_ADMIN,
  ROLE_MASTER_DATA_ADMIN,
  ROLE_CLIENT,
  ROLE_ACCOUNT,
  ROLE_EMPLOYEE_MANAGER,
  ROLE_EMPLOYEE_MEMBER,
  ROLE_BDM,
];
const TIMESHEET_OWNERS = [ROLE_EMPLOYEE_MANAGER, ROLE_EMPLOYEE_MEMBER];
const TIMESHEET_EDITORS = [ROLE_ADMIN, ROLE_EMPLOYEE_MANAGER, ROLE_EMPLOYEE_MEMBER];
const APPROVERS = [ROLE_ADMIN, ROLE_EMPLOYEE_MANAGER];
const EXPORTERS = [ROLE_ADMIN, ROLE_EMPLOYEE_MANAGER, ROLE_EMPLOYEE_MEMBER, ROLE_BDM, ROLE_ACCOUNT];

const action = (key, label, roles) => ({ key, label, kind: "action", roles });

const buildCatalog = () => [
  // Side-menu pages. routes.js has already dropped switched-off modules (IQMS, Knowledge, ...), so
  // they never appear here and can never rank as "least used".
  ...routes
    .filter((route) => route.type === "collapse" && route.route)
    .map((route) => ({ key: route.key, label: route.name, kind: "page", route: route.route, roles: route.allowedRoles })),
  { key: "change-password", label: "Change Password", kind: "page", route: "/change-password", roles: ALL_ROLES },
  action(FEATURE_ADD_ACTIVITY, "Add Activity", TIMESHEET_OWNERS),
  action(FEATURE_ADD_MULTIPLE_ACTIVITIES, "Add Multiple Activities", TIMESHEET_OWNERS),
  action(FEATURE_ADD_LEAVE, "Add Leave", TIMESHEET_OWNERS),
  action(FEATURE_ACTIVITY_EDITED, "Activity edited", TIMESHEET_EDITORS),
  action(FEATURE_ACTIVITY_DELETED, "Activity deleted", TIMESHEET_EDITORS),
  action(FEATURE_APPROVE, "Approve", APPROVERS),
  action(FEATURE_REJECT, "Reject", APPROVERS),
  action(FEATURE_CLIENT_APPROVE, "Approved by Client", APPROVERS),
  action(FEATURE_EXPORT_CONCISE, "Export - Concise", EXPORTERS),
  action(FEATURE_EXPORT_DETAILED, "Export - Detailed", EXPORTERS),
  action(FEATURE_EXPORT_CONSOLIDATED, "Export - Consolidated", APPROVERS),
  action(FEATURE_EXPORT_CONCISE_MONTHLY, "Export - Concise Monthly", [ROLE_ADMIN]),
  action(FEATURE_EXPORT_UTILIZATION, "Export - Utilization", [ROLE_ADMIN]),
  action(FEATURE_USER_GUIDE, "User Guide", ALL_ROLES),
];

// Built on first use, not at import: routes.js imports the screens, and they import this module, so
// reading routes while modules load would find it unfinished and crash the app.
let built = null;
const catalog = () => {
  if (!built) {
    const features = buildCatalog();
    built = {
      features,
      keys: new Set(features.map((feature) => feature.key)),
      pageByRoute: new Map(features.filter((feature) => feature.route).map((feature) => [feature.route, feature.key])),
    };
  }
  return built;
};

/**
 * Every page and action usage analytics counts, with the roles that can reach each one. The
 * Usage Analytics screen lists from here, so a feature nobody used still shows, with zero.
 *
 * @since NEXT_VERSION
 */
export const getFeatureCatalog = () => catalog().features;

// @since NEXT_VERSION
export const isTrackedFeature = (key) => catalog().keys.has(key);

// "/export-reports" -> "export-reports"; null for an address that is not a counted page.
// @since NEXT_VERSION
export const pageKeyForPath = (pathname) =>
  catalog().pageByRoute.get(`/${(pathname ?? "").split("/")[1] ?? ""}`) ?? null;
